import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import {
  Profile,
  BusinessProfile,
  DatingProfile,
  JobProfile,
  RoomType,
} from '@/types/profile'
import { createProfile, updateProfile, getProfile, CreateProfileRequest, ProfileDetailResponse } from '@/services/profileService'
import { useAuth } from '@/contexts/AuthContext'

type MyProfiles = {
  business: BusinessProfile | null
  dating: DatingProfile | null
  job: JobProfile | null
}

interface ProfileContextValue {
  myProfiles: MyProfiles
  saveProfile: (profile: Profile, roomId?: string, mode?: 'register' | 'edit', roleId?: number, pendingPhoto?: { fileBase64: string; fileName: string } | null) => Promise<void>
  getMyProfile: (type: RoomType) => Profile | null
  hasAnyProfile: () => boolean
}

const ProfileContext = createContext<ProfileContextValue | null>(null)

/**
 * 自分のプロフィールAPIレスポンスを3種類の枠に展開する。
 *
 * 本人が自分のプロフィールを取得した場合、バックエンドは
 * business / matchmaking / career の3セクションすべてを返す。
 * 登録済みのセクションのみを枠に格納する（名前が入っていれば登録済みとみなす）。
 */
const mapApiToProfiles = (data: ProfileDetailResponse): Partial<MyProfiles> => {
  const base = { id: data.userId, pr: data.prText, photoUrl: undefined, createdAt: new Date() }
  const result: Partial<MyProfiles> = {}

  if (data.bizName) {
    result.business = { ...base, type: 'business', name: data.bizName, companyName: data.bizCompanyName || '', position: data.bizPosition || '', businessContent: data.bizContent || '', comment: data.bizComment, wantToMatch: data.bizTargetIndustry, notWantToMatch: data.bizExcludeIndustry, contactEmail: data.bizEmail, contactPhone: data.bizTel, photoUrl: data.bizPhotoUrl }
  }
  if (data.loveNickname) {
    result.dating = { ...base, type: 'dating', name: data.loveNickname, age: data.loveAge || 0, occupation: data.loveJob || '', gender: (data.loveGender as any) || 'other', annualIncome: data.loveAnnualIncome || '', wantChildren: data.loveWantsKids || undefined, wantToMatch: data.loveTargetPerson, notWantToMatch: data.loveExcludePerson, contactEmail: data.loveContactInfo, contactPhone: data.loveTel, photoUrl: data.lovePhotoUrl, userNumber: data.userId }
  }
  if (data.careerName) {
    result.job = { ...base, type: 'job', name: data.careerName, companyName: data.careerPrevCompany || '', lastPosition: data.careerLastPosition || '', education: data.careerEducationLevel || '', schoolName: data.careerSchoolName || '', age: data.careerAge || 0, gender: (data.careerGender as any) || 'other', comment: data.careerComment, wantToMatch: data.careerTargetCompany, notWantToMatch: data.careerExcludeConditions, contactEmail: data.careerEmail, contactPhone: data.careerTel, photoUrl: data.careerPhotoUrl }
  }
  return result
}

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth()
  const [myProfiles, setMyProfiles] = useState<MyProfiles>({
    business: null,
    dating: null,
    job: null,
  })

  useEffect(() => {
    if (!user?.userId) {
      // ログアウト時はプロフィールをクリア
      setMyProfiles({ business: null, dating: null, job: null })
      return
    }
    // ユーザー変更時にまずクリアしてから再取得
    setMyProfiles({ business: null, dating: null, job: null })

    let cancelled = false
    const fetchMyProfile = async () => {
      try {
        // t_user_profile は user_id 主キーの1ユーザー1レコードで、
        // 本人取得時は3種類分のセクションがまとめて返る。
        const data = await getProfile(user.userId!)
        if (cancelled) return
        const mapped = mapApiToProfiles(data)
        setMyProfiles({ business: null, dating: null, job: null, ...mapped })
      } catch {
        // プロフィール未登録の場合はnullのまま
      }
    }

    fetchMyProfile()
    return () => {
      cancelled = true
    }
  }, [user?.userId])

  const saveProfile = async (profile: Profile, roomId?: string, mode?: 'register' | 'edit', roleId?: number, pendingPhoto?: { fileBase64: string; fileName: string } | null) => {
    // プロフィールデータをAPI用に変換
    const profileData: Record<string, any> = {
      pr: profile.pr,
      isPrPublic: true,
      isEmailPublic: false,
      isTelPublic: false,
    }

    //# イベントタイプに応じた項目を追加
    if (profile.type === 'business') {
      const bp = profile as BusinessProfile
      profileData.name = bp.name
      profileData.companyName = bp.companyName
      profileData.position = bp.position
      profileData.businessContent = bp.businessContent
      profileData.comment = bp.comment
      profileData.wantToMatch = bp.wantToMatch
      profileData.notWantToMatch = bp.notWantToMatch
      profileData.contactEmail = bp.contactEmail
      profileData.contactPhone = bp.contactPhone
      if (!pendingPhoto && bp.photoUrl && !bp.photoUrl.startsWith('data:')) profileData.photoUrl = bp.photoUrl
    } else if (profile.type === 'dating') {
      const dp = profile as DatingProfile
      profileData.name = dp.name
      profileData.age = dp.age
      profileData.occupation = dp.occupation
      profileData.gender = dp.gender
      profileData.annualIncome = dp.annualIncome
      profileData.wantChildren = dp.wantChildren
      profileData.wantToMatch = dp.wantToMatch
      profileData.notWantToMatch = dp.notWantToMatch
      profileData.contactEmail = dp.contactEmail
      profileData.contactPhone = dp.contactPhone
      if (!pendingPhoto && dp.photoUrl && !dp.photoUrl.startsWith('data:')) profileData.photoUrl = dp.photoUrl
    } else if (profile.type === 'job') {
      const jp = profile as JobProfile
      profileData.name = jp.name
      profileData.companyName = jp.companyName
      profileData.lastPosition = jp.lastPosition
      profileData.education = jp.education
      profileData.schoolName = jp.schoolName
      profileData.age = jp.age
      profileData.gender = jp.gender
      profileData.comment = jp.comment
      profileData.wantToMatch = jp.wantToMatch
      profileData.notWantToMatch = jp.notWantToMatch
      profileData.contactEmail = jp.contactEmail
      profileData.contactPhone = jp.contactPhone
      if (!pendingPhoto && jp.photoUrl && !jp.photoUrl.startsWith('data:')) profileData.photoUrl = jp.photoUrl
    }

    const request: CreateProfileRequest = {
      eventType: profile.type === 'dating' ? 'matchmaking' : profile.type === 'job' ? 'career' : 'business',
      profileData,
      roomId,
      roleId,
      ...(pendingPhoto ? { imageBase64: pendingPhoto.fileBase64, imageFileName: pendingPhoto.fileName } : {}),
    }

    if (mode === 'edit') {
      await updateProfile(request)
    } else {
      await createProfile(request)
    }

    // ローカルステートも更新
    setMyProfiles((prev) => ({
      ...prev,
      [profile.type]: profile,
    }))
  }

  const getMyProfile = (type: RoomType): Profile | null => {
    return myProfiles[type]
  }

  const hasAnyProfile = (): boolean => {
    return Object.values(myProfiles).some((p) => p !== null)
  }

  return (
    <ProfileContext.Provider value={{ myProfiles, saveProfile, getMyProfile, hasAnyProfile }}>
      {children}
    </ProfileContext.Provider>
  )
}

export const useMyProfile = () => {
  const context = useContext(ProfileContext)
  if (!context) throw new Error('useMyProfile must be used within ProfileProvider')
  return context
}
